import axios from 'axios'
import store from '@/store/DDMasters'
import system from '@/store/system'
import conf from '@/rahmen/conf'
import pako from 'pako'

let _onAuthErrorFlg = false
let _errorLocation = ''

/**
 * @file HTTP通信用クラス
 * @namespace rahmen
 */
/**
 * @module HttpClient
 */
export default class {
  /**
   * @constructor
   */
  constructor (kinoId) {
    this.httpClient = axios.create({
      withCredentials: true
    })
    this.alternateKinoId = kinoId
  }

  /**
   * GETメソッド
   * @param {String} url - リクエスト先URL
   * @returns {Promise<Object>}
   */
  get (url) {
    const token = store.state.authjwttoken
    const kinoId = this.alternateKinoId || system.state.currentKinoId

    return new Promise((resolve, reject) => {
      this.httpClient.interceptors.request.use((config) => {
        config.headers.Authorization = `Bearer ${token}`
        config.headers['X-ACCESS-KINO-ID'] = kinoId
        config.headers['Content-Type'] = 'application/json'
        console.info('httpClient.get', url, config)
        return config
      }, function (error) {
        return Promise.reject(error)
      })

      this.httpClient.get(url)
        .then((e) => {
          console.log(e)
          if (e.data === undefined) {
            reject(e)
            return
          }
          resolve(e.data)
        })
        .catch((e) => {
          // console.error(e)
          if (e.response && [401, 403].includes(e.response.status)) {
            _onAuthErrorFlg = true
            _errorLocation = window.location.href
            window.location.href = '/#/'
          }
          reject(e)
        })
    })
  }

  /**
   * ファイル(Blob)要求 (HTTP-GET)
   * @param {String} url - リクエスト先URL
   * @returns {Promise<Blob>}
   */
  requestFile (url) {
    const token = store.state.authjwttoken
    const kinoId = system.state.currentKinoId

    return new Promise((resolve, reject) => {
      this.httpClient.interceptors.request.use((config) => {
        config.headers.Authorization = `Bearer ${token}`
        config.headers['X-ACCESS-KINO-ID'] = kinoId
        console.info('httpClient.requestFile', url, config)
        return config
      }, function (error) {
        return Promise.reject(error)
      })

      this.httpClient.get(url, {
        responseType: 'blob'
      })
        .then((e) => {
          console.log(e)
          if (e.data === undefined) {
            reject(e)
            return
          }
          resolve(e.data)
        })
        .catch((e) => {
          // console.error(e)
          if (e.response && [401, 403].includes(e.response.status)) {
            _onAuthErrorFlg = true
            _errorLocation = window.location.href
            window.location.href = '/#/'
          }
          reject(e)
        })
    })
  }

  /**
   * ファイル(Blob)要求 (HTTP-POST)
   * @param {String} url - リクエスト先URL
   * @param {Object} formData - POSTデータ
   * @returns {Promise<Blob>}
   */
  requestFileByPost (url, formData) {
    return new Promise((resolve, reject) => {
      this.requestFileByPostReturnData(url, formData).then((e) => {
        console.log(e)
        if (e.data === undefined) {
          reject(e)
          return
        }
        resolve(e.data)
      })
        .catch((e) => {
          console.error(e)
          if (e.response && e.response.status === 401) {
            alert(conf.unauthorizedMessage)
          }
          reject(e)
        })
    })
  }

  /**
   * ファイル(Blob)要求 (HTTP-POST)
   * @param {String} url - リクエスト先URL
   * @param {Object} formData - POSTデータ
   * @returns {Promise<Blob>}
   */
  requestFileByPostReturnData (url, formData) {
    const token = store.state.authjwttoken
    const kinoId = system.state.currentKinoId

    return new Promise((resolve, reject) => {
      this.httpClient.interceptors.request.use((config) => {
        config.headers.Authorization = `Bearer ${token}`
        config.headers['X-ACCESS-KINO-ID'] = kinoId
        console.info('httpClient.requestFileByPost', url, config)
        return config
      }, function (error) {
        return Promise.reject(error)
      })

      this.httpClient.post(url, {
        formData: JSON.stringify(formData)
      },
      {
        responseType: 'blob'
      })
        .then((e) => {
          console.log(e)
          if (e.data === undefined) {
            reject(e)
            return
          }
          resolve(e)
        })
        .catch((e) => {
          // console.error(e)
          if (e.response && [401, 403].includes(e.response.status)) {
            _onAuthErrorFlg = true
            _errorLocation = window.location.href
            window.location.href = '/#/'
          }
          reject(e)
        })
    })
  }

  /**
   * POSTメソッド
   * @param {String} url - リクエスト先URL
   * @param {Object} formData - POSTデータ
   * @param {boolean} useCompress - true: POSTするデータを圧縮する, false: POSTするデータを圧縮しない
   * @returns {Promise<Object>}
   */
  post (url, formData, useCompress = false) {
    const token = store.state.authjwttoken
    const kinoId = system.state.currentKinoId

    return new Promise((resolve, reject) => {
      this.httpClient.interceptors.request.use((config) => {
        config.headers.Authorization = `Bearer ${token}`
        config.headers['X-ACCESS-KINO-ID'] = kinoId
        config.headers['Content-Type'] = 'application/json'
        console.info('httpClient.post', url, config)
        return config
      }, function (error) {
        return Promise.reject(error)
      })

      this.httpClient.post(url, {
        formData: useCompress ? this.compressFormData(formData) : JSON.stringify(formData)
      })
        .then((e) => {
          console.log(e)
          if (e.data === undefined) {
            reject(e)
            return
          }
          resolve(e.data)
        })
        .catch((e) => {
          // console.error(e)
          if (e.response && [401, 403].includes(e.response.status)) {
            _onAuthErrorFlg = true
            _errorLocation = window.location.href
            window.location.href = '/#/'
          }
          reject(e)
        })
    })
  }

  /**
   * PUTメソッド
   * @param {String} url - リクエスト先URL
   * @param {Object} formData - PUTデータ
   * @param {boolean} useCompress - true: POSTするデータを圧縮する, false: POSTするデータを圧縮しない
   * @returns {Promise<Object>}
   */
  put (url, formData, useCompress = false) {
    const token = store.state.authjwttoken
    const kinoId = system.state.currentKinoId

    return new Promise((resolve, reject) => {
      this.httpClient.interceptors.request.use((config) => {
        config.headers.Authorization = `Bearer ${token}`
        config.headers['X-ACCESS-KINO-ID'] = kinoId
        config.headers['Content-Type'] = 'application/json'
        console.info('httpClient.put', url, config)
        return config
      }, function (error) {
        return Promise.reject(error)
      })

      this.httpClient.put(url, {
        formData: useCompress ? this.compressFormData(formData) : JSON.stringify(formData)
      })
        .then((e) => {
          console.log(e)
          if (e.data === undefined) {
            reject(e)
            return
          }
          resolve(e.data)
        })
        .catch((e) => {
          // console.error(e)
          if (e.response && [401, 403].includes(e.response.status)) {
            _onAuthErrorFlg = true
            _errorLocation = window.location.href
            window.location.href = '/#/'
          }
          reject(e)
        })
    })
  }

  /**
   * DELETEメソッド
   * @param {String} url - リクエスト先URL
   * @returns {Promise<Object>}
   */
  delete (url) {
    const token = store.state.authjwttoken
    const kinoId = system.state.currentKinoId

    return new Promise((resolve, reject) => {
      this.httpClient.interceptors.request.use((config) => {
        config.headers.Authorization = `Bearer ${token}`
        config.headers['X-ACCESS-KINO-ID'] = kinoId
        config.headers['Content-Type'] = 'application/json'
        console.info('httpClient.delete', url, config)
        return config
      }, function (error) {
        return Promise.reject(error)
      })

      this.httpClient.delete(url)
        .then((e) => {
          console.log(e)
          if (e.data === undefined) {
            reject(e)
            return
          }
          resolve(e.data)
        })
        .catch((e) => {
          // console.error(e)
          if (e.response && [401, 403].includes(e.response.status)) {
            _onAuthErrorFlg = true
            _errorLocation = window.location.href
            window.location.href = '/#/'
          }
          reject(e)
        })
    })
  }

  getAuthErrorFlg () {
    return _onAuthErrorFlg
  }

  clearError () {
    _onAuthErrorFlg = false
    _errorLocation = ''
  }

  getErrorLocation () {
    return _errorLocation
  }

  /**
   * FormData圧縮
   * @param {Object} formData
   * @returns {String}
   */
  compressFormData (formData) {
    const json = JSON.stringify(formData)

    // 100KB未満ならそのまま
    if (json.length < 100000) {
      return json
    }

    console.info('gzip compress start', json.length)

    // gzip圧縮
    const compressed = pako.gzip(json)

    // Uint8Array → binary string
    let binary = ''

    compressed.forEach(byte => {
      binary += String.fromCharCode(byte)
    })

    // Base64化
    const base64 = btoa(binary)

    console.info('gzip compress end', base64.length)

    return 'gzip-base64:' + base64
  }
}
